import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast } from "sonner";
import { Plus, Trash2, Tag, Check, X as XIcon } from "lucide-react";

export default function AdminDiscounts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: "", percent: 10, active: true, description: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/discount-codes");
      setRows(r.data || []);
    } catch {
      toast.error("Couldn't load discount codes");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e?.preventDefault();
    const code = form.code.trim().toUpperCase();
    if (!code || code.length < 2) return toast.error("Code must be at least 2 chars");
    const pct = Number(form.percent);
    if (!(pct > 0) || pct > 100) return toast.error("Percent must be between 0 and 100");
    setSaving(true);
    try {
      await api.post("/admin/discount-codes", {
        code,
        percent: pct,
        active: !!form.active,
        description: form.description.trim(),
      });
      toast.success(`Code ${code} created`);
      setForm({ code: "", percent: 10, active: true, description: "" });
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create code");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/admin/discount-codes/${row.code}`, { active: !row.active });
      toast.success(`${row.code} ${!row.active ? "enabled" : "disabled"}`);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const updatePercent = async (row, percent) => {
    const pct = Number(percent);
    if (!(pct >= 0 && pct <= 100)) return toast.error("0–100 only");
    try {
      await api.patch(`/admin/discount-codes/${row.code}`, { percent: pct });
      toast.success("Saved");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete ${row.code}? Existing orders that used this code keep their discount, but new orders can't redeem it.`)) return;
    try {
      await api.delete(`/admin/discount-codes/${row.code}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-text)]">Discount codes</h1>
          <p className="text-sm muted-text mt-1">
            Percent off products only (services like delivery/install stay full price). Customers type the code on the Summary step.
          </p>
        </div>
        <button
          data-testid="new-discount-btn"
          onClick={() => setShowForm((v) => !v)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-sm brand-bg text-white"
        >
          <Plus className="w-4 h-4" /> New code
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={create}
          data-testid="new-discount-form"
          className="bg-white rounded-2xl border-2 border-[var(--brand-green)] p-4 mb-5 space-y-3"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-semibold muted-text">Code</label>
              <input
                data-testid="form-code"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="e.g. SAVE10"
                className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm mt-1 uppercase font-mono"
                maxLength={40}
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-semibold muted-text">Percent off</label>
              <div className="relative mt-1">
                <input
                  data-testid="form-percent"
                  type="number" min="1" max="100" step="0.5"
                  value={form.percent}
                  onChange={(e) => setForm({ ...form, percent: e.target.value })}
                  className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm pr-7"
                />
                <span className="absolute right-3 top-2 text-sm muted-text">%</span>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold muted-text">Status</label>
              <label className="flex items-center gap-2 mt-2.5 px-3 py-2 rounded-xl border-2 border-[var(--brand-border)] cursor-pointer">
                <input
                  data-testid="form-active"
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                <span className="text-sm">{form.active ? "Active" : "Disabled"}</span>
              </label>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold muted-text">Description (internal — not shown to customer)</label>
            <input
              data-testid="form-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Summer 2026 promo"
              className="w-full rounded-xl border-2 border-[var(--brand-border)] px-3 py-2 text-sm mt-1"
              maxLength={200}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => setShowForm(false)}
              className="px-4 py-2 rounded-full text-sm font-semibold border-2 border-[var(--brand-border)]">
              Cancel
            </button>
            <button
              data-testid="form-submit"
              type="submit"
              disabled={saving}
              className="px-5 py-2 rounded-full text-sm font-semibold brand-bg text-white disabled:opacity-50"
            >
              {saving ? "Creating…" : "Create code"}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="muted-text">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-[var(--brand-border)] p-8 text-center muted-text" data-testid="discounts-empty">
          <Tag className="w-6 h-6 mx-auto mb-2 muted-text" />
          <p>No discount codes yet. Click <strong>New code</strong> to create your first one.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="discount-list">
          {rows.map((row) => (
            <div
              key={row.code}
              data-testid={`discount-row-${row.code}`}
              className={`bg-white rounded-2xl border p-4 flex items-center gap-3 ${
                row.active ? "border-[var(--brand-border)]" : "border-[var(--brand-border)] opacity-60"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-bold font-mono text-[var(--brand-text)]">{row.code}</span>
                  {row.active ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                      style={{ background: "#dcfce7", color: "#166534" }}>active</span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                      style={{ background: "#e5e7eb", color: "#374151" }}>disabled</span>
                  )}
                  <span className="text-[11px] muted-text ml-auto">
                    {row.redemption_count || 0} redemption{(row.redemption_count || 0) === 1 ? "" : "s"}
                  </span>
                </div>
                {row.description && <p className="text-xs muted-text mt-0.5">{row.description}</p>}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <label className="flex items-center gap-1 text-xs">
                  <input
                    data-testid={`pct-${row.code}`}
                    type="number" min="0" max="100" step="0.5"
                    defaultValue={row.percent}
                    onBlur={(e) => {
                      const val = Number(e.target.value);
                      if (val !== row.percent) updatePercent(row, val);
                    }}
                    className="w-14 rounded-lg border-2 border-[var(--brand-border)] px-2 py-1 text-sm text-right tabular-nums"
                  />
                  <span className="muted-text">%</span>
                </label>
                <button
                  data-testid={`toggle-${row.code}`}
                  onClick={() => toggleActive(row)}
                  title={row.active ? "Disable" : "Enable"}
                  className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                    row.active
                      ? "border-[var(--brand-border)] hover:border-red-300 text-[var(--brand-text)]"
                      : "border-[var(--brand-border)] hover:border-[var(--brand-green)] brand-text"
                  }`}
                >
                  {row.active ? <XIcon className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                </button>
                <button
                  data-testid={`delete-${row.code}`}
                  onClick={() => remove(row)}
                  className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-red-200 text-red-600 hover:bg-red-50"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminLayout>
  );
}
